/**
 * MusicPlayer 封面/视频双层与淡入淡出（抽离自 MusicPlayer.js）
 */
import { useState, useRef, useEffect, useCallback } from 'react';

export function useCoverLayers(opts) {
  const {
    currentMusic,
    hasVideoInCurrentMusic,
    coverUrl,
    nextCoverUrl,
    fadeInOut,
    isSingleCoverMode,
    visualFadeOutToken,
    visualFadeInToken
  } = opts;

  const [coverLayerAUrl, setCoverLayerAUrl] = useState(null);
  const [coverLayerBUrl, setCoverLayerBUrl] = useState(null);
  const [coverLayerAType, setCoverLayerAType] = useState(null);
  const [coverLayerBType, setCoverLayerBType] = useState(null);
  const [coverLayerAOpacity, setCoverLayerAOpacity] = useState(1);
  const [coverLayerBOpacity, setCoverLayerBOpacity] = useState(0);
  const [coverLayerANoFadeIn, setCoverLayerANoFadeIn] = useState(false);
  const [coverLayerBNoFadeIn, setCoverLayerBNoFadeIn] = useState(false);

  const coverLayerInitRef = useRef(false);
  const coverActiveLayerRef = useRef('A');
  const coverFadeOutStartedRef = useRef(false);
  const coverFadeInRafRef = useRef(0);
  const lastCoverMusicIdRef = useRef(null);
  const lastVisualTypeRef = useRef(null);
  const noFadeResetRafRef = useRef(0);

  const scheduleCoverFadeIn = useCallback((layer) => {
    if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
    coverFadeInRafRef.current = requestAnimationFrame(() => {
      if (layer === 'A') setCoverLayerAOpacity(1);
      else setCoverLayerBOpacity(1);
    });
  }, []);

  const scheduleCoverNoFadeIn = useCallback((layer) => {
    if (noFadeResetRafRef.current) cancelAnimationFrame(noFadeResetRafRef.current);
    if (layer === 'A') {
      setCoverLayerANoFadeIn(true);
      setCoverLayerAOpacity(1);
    } else {
      setCoverLayerBNoFadeIn(true);
      setCoverLayerBOpacity(1);
    }
    noFadeResetRafRef.current = requestAnimationFrame(() => {
      if (layer === 'A') setCoverLayerANoFadeIn(false);
      else setCoverLayerBNoFadeIn(false);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
      coverFadeInRafRef.current = 0;
      if (noFadeResetRafRef.current) cancelAnimationFrame(noFadeResetRafRef.current);
      noFadeResetRafRef.current = 0;
    };
  }, []);

  useEffect(() => {
    const currentType = hasVideoInCurrentMusic ? 'video' : (coverUrl ? 'cover' : null);
    if (!currentType) {
      coverLayerInitRef.current = false;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = null;
      lastVisualTypeRef.current = null;
      setCoverLayerAUrl(null);
      setCoverLayerBUrl(null);
      setCoverLayerAType(null);
      setCoverLayerBType(null);
      setCoverLayerAOpacity(1);
      setCoverLayerBOpacity(0);
      return;
    }

    const musicId = currentMusic?.id ?? null;
    if (isSingleCoverMode) {
      coverLayerInitRef.current = true;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = musicId;
      lastVisualTypeRef.current = 'cover';
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType('cover');
      setCoverLayerAUrl(coverUrl);
      setCoverLayerBType(null);
      setCoverLayerBUrl(null);
      setCoverLayerAOpacity(0);
      setCoverLayerBOpacity(0);
      if (fadeInOut) {
        scheduleCoverFadeIn('A');
      } else {
        setCoverLayerAOpacity(1);
      }
      return;
    }
    if (!fadeInOut) {
      coverLayerInitRef.current = true;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = musicId;
      lastVisualTypeRef.current = currentType;
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      setCoverLayerAOpacity(1);
      setCoverLayerBOpacity(0);
      return;
    }

    const activeLayer = coverActiveLayerRef.current;
    if (musicId && musicId === lastCoverMusicIdRef.current) {
      if (activeLayer === 'A') {
        setCoverLayerAType(currentType);
        setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
        setCoverLayerBType(nextCoverUrl ? 'cover' : null);
        setCoverLayerBUrl(nextCoverUrl || null);
      } else {
        setCoverLayerBType(currentType);
        setCoverLayerBUrl(currentType === 'cover' ? coverUrl : null);
        setCoverLayerAType(nextCoverUrl ? 'cover' : null);
        setCoverLayerAUrl(nextCoverUrl || null);
      }
      return;
    }

    lastCoverMusicIdRef.current = musicId;
    const prevVisualType = lastVisualTypeRef.current;
    lastVisualTypeRef.current = currentType;
    coverFadeOutStartedRef.current = false;
    if (!coverLayerInitRef.current) {
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      setCoverLayerAOpacity(0);
      setCoverLayerBOpacity(0);
      coverLayerInitRef.current = true;
      scheduleCoverFadeIn('A');
      return;
    }

    const nextActive = activeLayer === 'A' ? 'B' : 'A';
    coverActiveLayerRef.current = nextActive;
    setCoverLayerAOpacity(0);
    setCoverLayerBOpacity(0);
    const noFadeInForCrossType = !!(prevVisualType && prevVisualType !== currentType);
    if (nextActive === 'A') {
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      if (noFadeInForCrossType) scheduleCoverNoFadeIn('A');
      else scheduleCoverFadeIn('A');
    } else {
      setCoverLayerBType(currentType);
      setCoverLayerBUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerAType(nextCoverUrl ? 'cover' : null);
      setCoverLayerAUrl(nextCoverUrl || null);
      if (noFadeInForCrossType) scheduleCoverNoFadeIn('B');
      else scheduleCoverFadeIn('B');
    }
  }, [currentMusic?.id, coverUrl, nextCoverUrl, fadeInOut, hasVideoInCurrentMusic, isSingleCoverMode, scheduleCoverFadeIn, scheduleCoverNoFadeIn]);

  useEffect(() => {
    if (!fadeInOut || !visualFadeOutToken) return;
    coverFadeOutStartedRef.current = true;
    if (coverActiveLayerRef.current === 'A') setCoverLayerAOpacity(0);
    else setCoverLayerBOpacity(0);
  }, [visualFadeOutToken, fadeInOut]);

  useEffect(() => {
    if (!fadeInOut || !visualFadeInToken) return;
    if (isSingleCoverMode) {
      coverActiveLayerRef.current = 'A';
      setCoverLayerANoFadeIn(false);
      setCoverLayerAOpacity(0);
      if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
      coverFadeInRafRef.current = requestAnimationFrame(() => {
        setCoverLayerAOpacity(1);
      });
      return;
    }
    if (coverActiveLayerRef.current === 'A' && coverLayerANoFadeIn) {
      scheduleCoverNoFadeIn('A');
      return;
    }
    if (coverActiveLayerRef.current === 'B' && coverLayerBNoFadeIn) {
      scheduleCoverNoFadeIn('B');
      return;
    }
    scheduleCoverFadeIn(coverActiveLayerRef.current);
  }, [visualFadeInToken, fadeInOut, isSingleCoverMode, scheduleCoverFadeIn, scheduleCoverNoFadeIn, coverLayerANoFadeIn, coverLayerBNoFadeIn]);

  return {
    coverLayerAUrl,
    coverLayerBUrl,
    coverLayerAType,
    coverLayerBType,
    coverLayerAOpacity,
    coverLayerBOpacity,
    coverLayerANoFadeIn,
    coverLayerBNoFadeIn,
    setCoverLayerAOpacity,
    setCoverLayerBOpacity,
    coverFadeOutStartedRef,
    coverActiveLayerRef
  };
}
